import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useParamsFilter } from "../../../../hooks/useParamsFilter";
import { useDates } from "../../../../hooks/useDates";
import { useFormatNumber } from "../../../../hooks/useFormatNumber";
import { PurchaseInvoiceDetail } from "../../../../interfaces/Purchases/Invoices/purchaseInvoices";

export const usePurchasesInvoiceDetailsColumns = () => {
    const [t] = useTranslation("global");
    const { TimeConverter } = useDates();
    const { setFormatNumberFromTable } = useFormatNumber();
    const columnsPurchasesInvoiceDetails: GridColDef<PurchaseInvoiceDetail>[] = [
        { field: 'purchase_invoice_name', headerName: t("purchaseinvoices.fields.purchase_invoice_name"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'item_code',
            headerName: t("items.fields.item_code"),
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'item_description',
            headerName: t("purchaseinvoicesdetails.fields.concept"),
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'quantity',
            headerName: t("purchaseinvoicesdetails.fields.quantity"),
            type: 'number',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            valueGetter: (value: any) => {
                return roundToTwoDecimals(value.value)
            },
            cellClassName: "my-custom-cell"
        },
        {
            field: 'price_by_unit',
            headerName: t("purchaseinvoicesdetails.fields.price"),
            type: 'number',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'tariff_fraction_code',
            headerName: t("tarifffractions.title-view"),
            renderCell: (params: GridRenderCellParams<any, Date>) => (
                <>{params.row.tariff_fraction_code} - {params.row.tariff_fraction_specific_description}</>
            ),
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'unit_code',
            headerName: t("purchaseinvoicesdetails.fields.unit_code"),
            type: 'number',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'tax_name',
            headerName: t("taxes.title-view"),
            renderCell: (params: GridRenderCellParams<any, Date>) => (
                <div>
                    %{params.row.tax_percent} - {params.row.tax_name}
                </div>
            ),
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'retention_name',
            headerName: t("retentions.title-view"),
            renderCell: (params: GridRenderCellParams<any, Date>) => (
                <div>
                    {params.row.retention_percent}% - {params.row.retention_name}
                </div>
            ),
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'discount',
            headerName: t("purchaseinvoicesdetails.fields.discount"),
            type: 'number',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'total_amount',
            headerName: t("purchaseinvoicesdetails.fields.subtotal_amount"),
            type: 'number',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            cellClassName: "my-custom-cell"
        },
        {
            field: 'project_name', headerName: t("projects.fields.project_name"), headerClassName: 'header-grid-table', flex: 1,
        },
        { field: 'uuid', headerName: t("purchaseinvoices.fields.uuid"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'purchase_invoice_date', headerName: t("purchaseinvoices.fields.purchase_invoice_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
        { field: 'supplier_business_name', headerName: t("purchaseinvoices.fields.supplier_business_name"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'invoice_doc_status_name', headerName: t("purchaseinvoices.fields.invoice_doc_status_name"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'currency_code', headerName: t("currencies.title-view"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'purchase_invoice_subtotal_amount', headerName: t("purchaseinvoices.fields.subtotal_amount"), headerClassName: 'header-grid-table', flex: 1, type: 'number', align: "left", headerAlign: "left",
            valueGetter(params) {
                return setFormatNumberFromTable(params.value ?? 0);
            },
        },
        {
            field: 'tax_amount', headerName: t("purchaseinvoices.fields.tax_amount"), headerClassName: 'header-grid-table', flex: 1, type: 'number', align: "left", headerAlign: "left",
            valueGetter(params) {
                return setFormatNumberFromTable(params.value ?? 0);
            },
        },
        {
            field: 'retention_amount', headerName: t("purchaseinvoices.fields.retention_amount"), headerClassName: 'header-grid-table', flex: 1, type: 'number', align: "left", headerAlign: "left",
            valueGetter(params) {
                return setFormatNumberFromTable(params.value ?? 0);
            },
        },
        {
            field: 'purchase_invoice_total_amount', headerName: t("purchaseinvoices.fields.total_amount"), headerClassName: 'header-grid-table', flex: 1, type: 'number', align: "left", headerAlign: "left",
            valueGetter(params) {
                return setFormatNumberFromTable(params.value ?? 0);
            },
        },
        {
            field: 'total_company_currency_amount', headerName: t("companies.configuration.fields.total_company_currency_amount"), headerClassName: 'header-grid-table', flex: 1,
            valueGetter(params) {
                return setFormatNumberFromTable(params.value ?? 0);
            },
        },
        { field: 'user_mail_send', headerName: t("purchaseinvoices.fields.user_mail_send"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'date_mail_send', headerName: t("purchaseinvoices.fields.date_mail_send"), headerClassName: 'header-grid-table', flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
        {
            field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
        { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
    ];

    function roundToTwoDecimals(num: number): number {
        return Math.round(num * 100) / 100;
    }

    return {
        columnsPurchasesInvoiceDetails
    };
}